import React, { useCallback, useId } from 'react';
import { AppIcon } from '../../../../components/icons/AppIcon';
import { Lead } from '../../../../types';
import { MarkdownEditor } from '../../../kanban/components/lead-detail/MarkdownEditor';
import { notify } from '../../../../utils/toast';
import { generateSummaryForLead } from '../../../../services/geminiService';

interface ContactPanelObservationsProps {
    isOpen: boolean;
    onToggle: () => void;
    currentLead: Lead | undefined;
    onLeadUpdate: (lead: Lead) => void;
}

export const ContactPanelObservations: React.FC<ContactPanelObservationsProps> = ({
    isOpen,
    onToggle,
    currentLead,
    onLeadUpdate
}) => {
    const panelId = useId();
    const hasLead = !!currentLead;
    const hasNotes = Boolean(currentLead?.observations?.trim());

    const handleSave = useCallback((newObservations: string) => {
        if (!currentLead) return;
        onLeadUpdate({ ...currentLead, observations: newObservations });
    }, [currentLead, onLeadUpdate]);

    return (
        <section>
            <button
                type="button"
                onClick={onToggle}
                className="flex min-h-11 w-full items-center justify-between px-3 py-2"
                aria-expanded={isOpen}
                aria-controls={panelId}
            >
                <div className="flex items-center gap-2 chat-section-heading">
                    <span>Observações</span>
                    {hasNotes && (
                        <span className="chat-count" aria-label="Há observações">1</span>
                    )}
                </div>
                <AppIcon
                    name="chevron-down"
                    className={`chat-accordion-chevron ${isOpen ? '' : 'chat-accordion-chevron--closed'}`}
                    aria-hidden="true"
                />
            </button>

            {isOpen && (
                <div id={panelId} className="bg-card px-3 pb-3">
                    {hasLead ? (
                        <MarkdownEditor
                            initialValue={currentLead.observations || ''}
                            onSave={handleSave}
                            maxLength={5000}
                            onGenerateAISummary={async () => {
                                try {
                                    return await generateSummaryForLead(currentLead);
                                } catch (error: unknown) {
                                    const message = error instanceof Error
                                        ? error.message
                                        : 'Falha ao analisar a conversa com IA.';
                                    notify.error(message);
                                    throw error;
                                }
                            }}
                        />
                    ) : (
                        <aside
                            className="flex items-start gap-2.5 rounded-lg border border-info/25 bg-info-bg p-3"
                            aria-label="Observações indisponíveis"
                        >
                            <AppIcon name="info" className="mt-0.5 h-4 w-4 shrink-0 text-info" aria-hidden="true" />
                            <p className="text-caption leading-relaxed">
                                Crie uma{' '}
                                <strong className="font-medium text-default">negociação</strong>
                                {' '}primeiro para poder adicionar observações.
                            </p>
                        </aside>
                    )}
                </div>
            )}
        </section>
    );
};
